// Display grouping of the hours and cost figures, in the UI language:
// en "12,345.5", fr "12 345,5". Display only: editors keep a raw number and
// Excel cells stay numbers, so no typed or exported value is re-parsed.
// The default i18next instance is the app's (src/i18n.js initialises it);
// importing it directly keeps this helper free of that module's side effects.
import i18n from "i18next";

const formatters = new Map();

const localeOf = (lang) => (String(lang || "").startsWith("fr") ? "fr-FR" : "en-US");

export const uiLanguage = () => i18n?.resolvedLanguage || i18n?.language || "en";

const formatterFor = (locale, maxFractionDigits) => {
  const key = `${locale}|${maxFractionDigits}`;
  if (!formatters.has(key)) {
    formatters.set(key, new Intl.NumberFormat(locale, { maximumFractionDigits: maxFractionDigits }));
  }
  return formatters.get(key);
};

const finiteOrNull = (value) => {
  if (value == null || value === "" || typeof value === "boolean") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

export const formatGroupedNumber = (value, lang = uiLanguage(), maxFractionDigits = 2) => {
  const n = finiteOrNull(value);
  if (n == null) return "";
  // A negative that rounds to zero would print "-0".
  const shown = Number(n.toFixed(maxFractionDigits)) === 0 ? 0 : n;
  return formatterFor(localeOf(lang), maxFractionDigits).format(shown);
};

// The PPM is mono-currency EUR. French puts a non-breaking space before the
// symbol; English writes it right after the number, as the cost columns did.
export const formatCurrencyAmount = (value, lang = uiLanguage(), maxFractionDigits = 2) => {
  const text = formatGroupedNumber(value, lang, maxFractionDigits);
  if (!text) return "";
  return localeOf(lang) === "fr-FR" ? `${text} €` : `${text}€`;
};

// A natively rendered "12345.5 hours" (Bryntum cell, plural string) with its
// number grouped in the UI language ("12,345.5 hours" / "12 345,5 hours"); the
// label and rounding are kept. Only an ungrouped run of 4+ integer digits is
// touched, so text already grouped (formatWork / formatDuration) passes
// through. A bare number is grouped as a whole.
const NATIVE_NUMBER = /^(\s*-?)(\d{4,})(?:[.,](\d+))?(?!\d)/;

export const groupNativeNumberText = (text) => {
  if (typeof text === "number") return Number.isFinite(text) ? groupNativeNumberText(String(text)) : text;
  if (typeof text !== "string") return text;
  const match = NATIVE_NUMBER.exec(text);
  if (!match) return text;
  const [whole, sign, integer, fraction = ""] = match;
  const n = Number(`${sign.trim()}${integer}${fraction ? `.${fraction}` : ""}`);
  const leading = sign.slice(0, sign.length - sign.trimStart().length);
  return `${leading}${formatGroupedNumber(n, undefined, fraction.length)}${text.slice(whole.length)}`;
};
